import axios, {
  AxiosError,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from "axios";
import queryString from "query-string";
import { useAuth } from "~/store";
import { authServices } from "./auth";

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const baseConfig: CreateAxiosDefaults = {
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: (params) =>
    queryString.stringify(params, { 
      skipEmptyString: true,
      arrayFormat: 'bracket' 
    }),
};

export const baseService = axios.create(baseConfig);
export const instanceWithoutInterceptors = axios.create(baseConfig);

let isRefreshing: boolean = false;
let failedQueue: {
  resolve: (value: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

baseService.interceptors.request.use(
  (config) => {
    const tokens = useAuth.getState().tokens;

    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens?.accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

baseService.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest: CustomAxiosRequestConfig | undefined = error.config;

    const refreshToken = useAuth.getState().tokens?.refreshToken;
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      refreshToken
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return baseService(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const tokens = await authServices.refreshToken(refreshToken);

        useAuth.setState({ tokens });

        originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
        processQueue(null, tokens.accessToken);

        return baseService(originalRequest);
      } catch (error) {
        processQueue(error, null);
        useAuth.getState().logout();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

export class BaseApi {
  protected get<T = any>(url: string, config?: any): Promise<ApiResponse<T>> {
    return baseService.get(url, config);
  }

  protected post<T = any>(
    url: string,
    data?: any,
    config?: any
  ): Promise<ApiResponse<T>> {
    return baseService.post(url, data, config);
  }

  protected put<T = any>(
    url: string,
    data?: any,
    config?: any
  ): Promise<ApiResponse<T>> {
    return baseService.put(url, data, config);
  }

  protected delete<T = any>(
    url: string,
    config?: any
  ): Promise<ApiResponse<T>> {
    return baseService.delete(url, config);
  }
}
